/* eslint-disable import/order -- this breaks somehow with different order */
import type {GetState} from '@/redux/typesNew';
import type {Response} from '@/redux/v1/hotels/inventory/models';

import {combineHotelParamsToBackendQuery} from '@/modules/common/utils/combine';
import {FETCH_INVENTORY} from '@/redux/v1/hotels/inventory/actionTypes';
import {
  getInventoryUnfiltered,
  selectTranslations,
} from '@/redux/v1/hotels/inventory/reducer';
import {resolveDestinationId} from '@/redux/v1/hotels/utils';
import {v4 as uuid} from '@lukeed/uuid';
import {ASYNC_TYPES} from '@travel-frontend/state-handling-sdk';
import {getCurrentUserId} from '@travel-frontend/user-id';
import axios from 'axios';
import {isPlainObject} from 'lodash';

type FetchInventoryParams = {
  initial: boolean;
  searchParams: any;
  filterParams: any;
};

let abortController: AbortController | null = null;

// translations are lifted to one top-level property, add them back to each category/variation for easier access
const mapInventory = (inventory: any, translations: Record<string, string>) =>
  inventory.map(({name, variations}: any) => ({
    name,
    translation: translations[name],
    // @ts-expect-error ...
    variations: variations?.map((variation) => ({
      ...variation,
      translation: translations[variation.value],
    })),
  }));

export const fetchInventory =
  ({initial, searchParams, filterParams}: FetchInventoryParams) =>
  // @ts-expect-error ...
  (dispatch) => {
    if (abortController) {
      abortController.abort();
      abortController = new AbortController();
    } else {
      abortController = new AbortController();
    }
    return dispatch({
      types: ASYNC_TYPES(FETCH_INVENTORY),
      callAPI: (getState: GetState) => {
        const storedInventoryUnfiltered = getInventoryUnfiltered(getState());
        const storedTranslations = selectTranslations(getState());

        const isInitialRequest =
          initial ||
          // initial request might yield empty unfiltered due to search params
          storedInventoryUnfiltered.length === 0;

        return resolveDestinationId(
          searchParams.destinationId,
          dispatch,
          getState
        )
          .then((resolvedDestinationId) =>
            axios.get<Response>(
              `${
                import.meta.env.VITE_SERVICE_BFF_URL
              }/hotel/inventory?initial=${String(
                isInitialRequest
              )}&${combineHotelParamsToBackendQuery({
                searchParams: {
                  ...searchParams,
                  destinationId: resolvedDestinationId,
                },
                filterParams,
                sortParams: null,
              })}`,
              {
                headers: {
                  'X-User-Id': getCurrentUserId(),
                },
                signal: abortController?.signal,
              }
            )
          )
          .then((response) => {
            const initialCategories = Array.isArray(
              response.data.initialCategories
            )
              ? response.data.initialCategories
              : [];
            const categories = Array.isArray(response.data.categories)
              ? response.data.categories
              : [];
            const translations = isPlainObject(response.data.translations)
              ? response.data.translations
              : {};

            if (isInitialRequest) {
              return {
                inventoryUnfiltered: mapInventory(
                  initialCategories,
                  translations
                ),
                inventoryFiltered: mapInventory(categories, translations),
                translations,
              };
            }
            return {
              inventoryUnfiltered: storedInventoryUnfiltered,
              inventoryFiltered: mapInventory(categories, storedTranslations),
              translations: storedTranslations,
            };
          });
      },
      meta: {
        requestId: uuid(),
      },
    });
  };
